import React, {
  Suspense,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import LoadingSpinner from "./components/Common/LoadingSpinner";
import Nav from "./components/Common/Nav";
import Footer from "./components/Common/Footer";
import classes from "./App.module.css";

import AuthContext from "./store/auth-context";
import ApiService from "./services/api.service";

const MakeADemand = React.lazy(() => import("./pages/MakeADemand"));
const MyDemands = React.lazy(() => import("./pages/MyDemands"));
const Profil = React.lazy(() => import("./pages/Profil"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const NotConnected = React.lazy(() => import("./pages/NotConnected"));
const Admin = React.lazy(() => import("./pages/Admin"));
const DemandeDetails = React.lazy(() => import("./pages/DemandeDetails"));
const DemandeDetailsUser = React.lazy(() =>
  import("./pages/DemandeDetailsUser")
);
const Export = React.lazy(() => import("./pages/Export"));

function App() {
  // Navigation data
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Authentication data
  const authCtx = useContext(AuthContext);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const regex = /[^A-Za-z0-9\-._~%]/;
    const ssoCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("sso="))
      ?.split("=")[1];

    if (ssoCookie) {
      setToken(
        regex.test(ssoCookie) ? window.location.search.substring(7) : ssoCookie
      );
    } else {
      setToken(window.location.search.substring(7));
    }
  }, []);

  const [isAdminMode, setIsAdminMode] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const loginRequest = useCallback(async () => {
    try {
      const res = await ApiService.login(token);
      if (!res.ok) {
        throw new Error("Erreur d'authentification");
      }

      const data = await res.json();
      if (res.ok && data["collection"].ntlm !== "") {
        authCtx.login(data["collection"].ntlm, data["collection"]);

        let isAdminModeTemp = document.cookie
          .split(";")
          .some((item) => item.trim().startsWith("gp="))
          ? document.cookie
              .split("; ")
              .find((row) => row.startsWith("gp="))
              ?.split("=")[1]
          : data["collection"].is_admin === "1"
          ? "true"
          : "false";

        var now = new Date();
        now.setFullYear(now.getFullYear() + 10);

        document.cookie =
          "sso=" +
          token +
          "; expires=" +
          now.toUTCString() +
          "; Secure; Path=/; SameSite=Lax";

        document.cookie =
          "gp=" + isAdminModeTemp + "; Secure; Path=/; SameSite=Lax";

        setIsAdminMode(isAdminModeTemp === "true");

        if (
          isAdminModeTemp === "true" &&
          !location.pathname.includes("admin")
        ) {
          navigate("/admin");
        } else {
          if (searchParams.has("token")) {
            searchParams.delete("token");
            setSearchParams(searchParams);
          }
        }
      }
    } catch (error) {
      console.log(error.message);
      setLoginError(error.message);
    }
  }, [
    token,
    authCtx,
    searchParams,
    setSearchParams,
    navigate,
    location.pathname,
  ]);

  if (!authCtx.isLoggedIn && loginError === null && token !== null) {
    loginRequest();
  }

  const switchModeHandler = (mode) => {
    const newMode = !isAdminMode ? "true" : "false";
    if (isAdminMode) {
      if (mode === "admin") {
        return;
      }
    } else {
      if (mode === "user") {
        return;
      }
    }
    document.cookie =
      "gp=" + newMode.toString() + "; Secure; Path=/; SameSite=Lax";
    setIsAdminMode(!isAdminMode);
  };

  return (
    <>
      <Nav
        switchMode={switchModeHandler}
        isAdminMode={isAdminMode}
        isAdmin={authCtx.userInfos.is_admin === "1"}
      />
      <Suspense
        fallback={
          <div className={classes.centered}>
            <LoadingSpinner />
          </div>
        }
      >
        <Routes>
          {authCtx.userInfos.is_admin === "1" && (
            <>
              <Route path="/" element={<Navigate to={"/admin"} />} />
              {authCtx.isLoggedIn && (
                <>
                  <Route path="/admin/*" element={<Admin />} />
                  <Route
                    path="/admin/demande/:demandeId"
                    element={<DemandeDetails />}
                  />
                  <Route path="/admin/export" element={<Export />} />
                </>
              )}
            </>
          )}

          {!authCtx.userInfos.is_admin === "1" && (
            <Route path="/" element={<Navigate to={"/accueil"} />} />
          )}

          <Route
            path="/accueil/*"
            element={
              authCtx.isLoggedIn ? (
                <Profil />
              ) : loginError !== null ? (
                <NotConnected />
              ) : (
                <LoadingSpinner />
              )
            }
          />
          <Route
            path="/faire-une-demande/*"
            element={
              authCtx.isLoggedIn ? (
                <MakeADemand />
              ) : loginError !== null ? (
                <NotConnected />
              ) : (
                <LoadingSpinner />
              )
            }
          />
          <Route
            path="/mes-demandes/*"
            element={
              authCtx.isLoggedIn ? (
                <MyDemands />
              ) : loginError !== null ? (
                <NotConnected />
              ) : (
                <LoadingSpinner />
              )
            }
          />
          <Route
            path="/mes-demandes/demande/:demandeId"
            element={<DemandeDetailsUser />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
}

export default App;
